import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Alert,
  Divider,
  LinearProgress,
  Card,
  CardContent,
  Grid
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import MusicNoteIcon from '@mui/icons-material/MusicNote';
import { useAudio } from '../context/AudioContext';

function Account() {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [subscriptionData, setSubscriptionData] = useState(null);

  useEffect(() => {
    // Fetch subscription data
    const fetchSubscriptionData = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('token');
        if (!token) {
          navigate('/login');
          return;
        }

        console.log('fetchSubscriptionData token: ', token);
        const baseUrl = window.location.origin.replace('3000', '8000').replace('3500', '8500');
        const response = await axios.get(`${baseUrl}/api/stripe/subscription-status`, {
          headers: { 'x-auth-token': token }
        });
        console.log('subscriptionData response: ', response.data);

        setSubscriptionData(response.data);
        console.log('subscriptionData: ', response.data);
        setError(null);
      } catch (err) {
        console.error('Error fetching subscription data:', err);
        setError('Failed to load subscription data. Please try again later.');
        if(err.response.status === 401) {
            navigate('/login');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchSubscriptionData();
  }, [isAuthenticated, navigate]);

  const handleUpgradeSubscription = () => {
    navigate('/subscription');
  };

  const calculateUsagePercentage = () => {
    if (!subscriptionData || !subscriptionData.subscriptionUsage || !subscriptionData.subscriptionMaxUsage) {
      return 0;
    }
    return Math.min(100, (subscriptionData.subscriptionUsage / subscriptionData.subscriptionMaxUsage) * 100);
  };

//   const formatDate = (timestamp) => {
//     if (!timestamp) return 'N/A';
//     const date = new Date(timestamp * 1000);
//     return date.toLocaleDateString('en-US', { 
//       year: 'numeric', 
//       month: 'long', 
//       day: 'numeric' 
//     });
//   };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Invalid Date';
  
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    }).format(date);
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ py: 8, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 8 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Account
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 4 }}>
          {error}
        </Alert>
      )}

      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Subscription Status
          </Typography>
          
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            {subscriptionData?.hasSubscription ? (
              <>
                <CheckCircleIcon color="success" sx={{ mr: 1 }} />
                <Typography variant="body1">
                  Active Subscription
                </Typography>
              </>
            ) : (
              <>
                <CancelIcon color="error" sx={{ mr: 1 }} />
                <Typography variant="body1">
                  No Active Subscription
                </Typography>
              </>
            )}
          </Box>

          {subscriptionData?.hasSubscription && (
            <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                Start Date: {subscriptionData.subscriptionStartTime
                    ? formatDate(new Date(subscriptionData.subscriptionStartTime * 1000))
                    : 'N/A'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                Renewal/End Date: {subscriptionData.subscriptionEndTime
                    ? formatDate(new Date(subscriptionData.subscriptionEndTime * 1000))
                    : 'N/A'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                Monthly Limit: {subscriptionData.subscriptionMaxUsage} songs
                </Typography>
            </Box>
          )}


          {subscriptionData?.hasSubscription ? (
            <Button 
              variant="outlined" 
              color="primary" 
              onClick={handleUpgradeSubscription}
              sx={{ mt: 2 }}
            >
              Manage Subscription
            </Button>
          ) : (
            <Button 
              variant="contained" 
              color="primary" 
              onClick={handleUpgradeSubscription}
              sx={{ mt: 2 }}
            >
              Upgrade to Premium
            </Button>
          )}
        </CardContent>
      </Card>

      {subscriptionData?.hasSubscription && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Usage
            </Typography>
            
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Songs Generated This Month
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {subscriptionData.subscriptionUsage || 0} of {subscriptionData.subscriptionMaxUsage || 0} songs
                </Typography>
              </Box>
              
              <LinearProgress 
                variant="determinate" 
                value={calculateUsagePercentage()} 
                sx={{ 
                  height: 10, 
                  borderRadius: 5,
                  backgroundColor: 'rgba(0, 0, 0, 0.1)',
                  '& .MuiLinearProgress-bar': {
                    borderRadius: 5,
                  }
                }}
              />
            </Box>
            
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Your usage resets on {subscriptionData.subscriptionEndTime ? formatDate(new Date(subscriptionData.subscriptionEndTime * 1000)) : 'N/A'}.
            </Typography>
          </CardContent>
        </Card>
      )}

      <Box sx={{ mt: 6, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          Need help? Contact our support team at jomakapps@gmail.com
        </Typography>
      </Box>
    </Container>
  );
}

export default Account; 